/* Landing page — industrial editorial. Sharp corners, 2px borders, hard shadows. */

.root {
  height: 100%;
  overflow-y: auto;
  overflow-x: clip;
  background: var(--color-bg);
  color: var(--color-ink);
  scroll-behavior: smooth;
}

@media (prefers-reduced-motion: reduce) {
  .root {
    scroll-behavior: auto;
  }
}

.skipLink {
  position: absolute;
  top: -48px;
  left: 12px;
  z-index: 100;
  padding: 10px 16px;
  background: var(--color-ink);
  color: var(--color-bg);
  font-family: var(--font-mono);
  font-size: 12px;
  text-decoration: none;
  transition: top 0.15s ease;
}

.skipLink:focus-visible {
  top: 12px;
}

/* -------------------------------- header -------------------------------- */

.header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: var(--color-bg);
  border-bottom: 2px solid var(--color-border);
}

.headerInner {
  max-width: 1160px;
  margin-inline: auto;
  padding-inline: clamp(20px, 5vw, 48px);
  height: 68px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brandMark {
  flex: none;
}

.brandName {
  font-family: var(--font-cond);
  font-weight: 700;
  font-size: 20px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.brandAi {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.1em;
  padding: 2px 6px;
  border: 1.5px solid var(--color-ink);
  color: var(--color-ink);
}

.nav {
  display: none;
  gap: 28px;
}

@media (min-width: 768px) {
  .nav {
    display: flex;
  }
}

.navLink {
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
  color: var(--color-sub);
  padding: 8px 2px;
  transition: color 0.15s ease;
}

.navLink:hover {
  color: var(--color-hivis);
}

.headerActions {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Secondary header action — outline twin of .headerCta; opens the web app. */
.headerLogin {
  font-family: var(--font-cond);
  font-weight: 600;
  font-size: 16px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--color-ink);
  padding: 10px 16px;
  min-height: 44px;
  border: 2px solid var(--color-ink);
  transition:
    transform 0.16s ease,
    box-shadow 0.16s ease;
}

.headerLogin:hover {
  transform: translate(-2px, -2px);
  box-shadow: 4px 4px 0 var(--color-hivis);
}

.headerLogin:active {
  transform: translate(0, 0);
  box-shadow: 1px 1px 0 var(--color-hivis);
}

.headerLogin,
.headerCta {
  white-space: nowrap;
}

@media (max-width: 480px) {
  .headerActions {
    gap: 6px;
  }

  .headerLogin,
  .headerCta {
    font-size: 14px;
    padding: 8px 10px;
  }

  /* The wordmark + two actions can't share 390px — keep the logo mark only. */
  .brandName,
  .brandAi {
    display: none;
  }
}

.headerCta {
  font-family: var(--font-cond);
  font-weight: 600;
  font-size: 16px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: var(--color-ink);
  color: var(--color-bg);
  padding: 10px 18px;
  min-height: 44px;
  border: 2px solid var(--color-ink);
  transition:
    transform 0.16s ease,
    box-shadow 0.16s ease;
}

.headerCta:hover {
  transform: translate(-2px, -2px);
  box-shadow: 4px 4px 0 var(--color-hivis);
}

.headerCta:active {
  transform: translate(0, 0);
  box-shadow: 1px 1px 0 var(--color-hivis);
}

/* --------------------------------- hero --------------------------------- */

.hero {
  position: relative;
  overflow: hidden;
  border-bottom: 2px solid var(--color-ink);
}

.heroInner {
  max-width: 1160px;
  margin-inline: auto;
  padding: clamp(56px, 11vh, 120px) clamp(20px, 5vw, 48px) 190px;
}

.eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--color-sub);
}

.eyebrowSquare {
  width: 9px;
  height: 9px;
  flex: none;
  background: var(--color-hivis);
}

.h1 {
  margin: 20px 0 0;
  font-family: var(--font-cond);
  font-weight: 700;
  font-size: clamp(3.1rem, 9.5vw, 8.2rem);
  line-height: 0.96;
  letter-spacing: -0.015em;
  text-transform: uppercase;
  color: var(--color-ink);
}

.line {
  display: block;
  overflow: hidden;
  padding-bottom: 0.09em;
  margin-bottom: -0.09em;
}

.lineInner {
  display: block;
  will-change: transform;
}

.accentWrap {
  position: relative;
  display: inline-block;
}

.chalk {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0.015em;
  width: 100%;
  height: 0.11em;
  transform-origin: left center;
}

.sub {
  max-width: 560px;
  margin: 26px 0 0;
  font-size: clamp(17px, 2vw, 20px);
  line-height: 1.55;
  color: var(--color-sub);
}

.ctaRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  margin-top: 34px;
}

.btnPrimary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-family: var(--font-cond);
  font-weight: 600;
  font-size: 19px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: var(--color-hivis);
  color: #14140f;
  border: 2px solid var(--color-ink);
  padding: 13px 26px;
  min-height: 52px;
  box-shadow: 4px 4px 0 var(--color-ink);
  transition:
    transform 0.16s ease,
    box-shadow 0.16s ease;
}

.btnPrimary:hover {
  transform: translate(-2px, -2px);
  box-shadow: 6px 6px 0 var(--color-ink);
}

.btnPrimary:active {
  transform: translate(2px, 2px);
  box-shadow: 1px 1px 0 var(--color-ink);
}

.btnGhost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-family: var(--font-cond);
  font-weight: 600;
  font-size: 19px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: transparent;
  color: var(--color-ink);
  border: 2px solid var(--color-ink);
  padding: 13px 26px;
  min-height: 52px;
  text-decoration: none;
  cursor: pointer;
  transition:
    transform 0.16s ease,
    box-shadow 0.16s ease,
    background 0.16s ease;
}

.btnGhost:hover {
  transform: translate(-2px, -2px);
  box-shadow: 4px 4px 0 var(--color-hivis);
  background: var(--color-surface);
}

.btnGhost:active {
  transform: translate(0, 0);
  box-shadow: 1px 1px 0 var(--color-hivis);
}

.trustRow {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  margin: 28px 0 0;
  padding: 0;
  list-style: none;
}

.trustRow li {
  position: relative;
  padding-left: 16px;
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-sub);
}

.trustRow li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 7px;
  height: 7px;
  transform: translateY(-50%);
  background: var(--color-green);
}

/* waveform strip pinned to the hero's bottom edge */

.waveStrip {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 96px;
  pointer-events: none;
}

.recChip {
  position: absolute;
  left: clamp(20px, 5vw, 48px);
  top: -8px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: var(--color-surface);
  border: 2px solid var(--color-ink);
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.1em;
  color: var(--color-ink);
  box-shadow: 3px 3px 0 var(--color-ink);
}

.recDot {
  width: 9px;
  height: 9px;
  flex: none;
  border-radius: 50%;
  background: #e5484d;
  animation: blink 1.3s ease-in-out infinite;
}

@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.25;
  }
}

.wave {
  position: absolute;
  inset: 12px 0 0;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 3px;
  overflow: hidden;
}

.waveBar {
  flex: none;
  width: 3px;
  background: var(--color-hivis);
  transform-origin: bottom;
  animation: pulse 1.15s ease-in-out infinite alternate;
}

@keyframes pulse {
  from {
    transform: scaleY(1);
  }
  to {
    transform: scaleY(0.28);
  }
}

/* -------------------------------- marquee -------------------------------- */

.marquee {
  overflow: hidden;
  background: var(--color-ink);
  color: var(--color-bg);
  padding-block: 14px;
}

.marqueeTrack {
  display: flex;
  width: max-content;
  animation: scroll 30s linear infinite;
}

.marqueeGroup {
  display: flex;
  flex: none;
}

.marqueeItem {
  display: inline-flex;
  align-items: center;
  gap: 2.2rem;
  padding-right: 2.2rem;
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.18em;
  white-space: nowrap;
}

.marqueeSquare {
  width: 8px;
  height: 8px;
  flex: none;
  background: var(--color-hivis);
}

@keyframes scroll {
  to {
    transform: translateX(-50%);
  }
}

/* -------------------------------- sections ------------------------------- */

.section {
  max-width: 1160px;
  margin-inline: auto;
  padding: clamp(72px, 10vw, 128px) clamp(20px, 5vw, 48px);
  scroll-margin-top: 70px;
}

.sectionAlt {
  background: var(--color-tape);
  border-block: 2px solid var(--color-border);
  scroll-margin-top: 70px;
}

.sectionAlt .section {
  scroll-margin-top: 0;
}

.sectionHead {
  margin-bottom: clamp(36px, 5vw, 56px);
}

.h2 {
  margin: 14px 0 0;
  font-family: var(--font-cond);
  font-weight: 700;
  font-size: clamp(2.1rem, 5vw, 3.6rem);
  line-height: 1.02;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  color: var(--color-ink);
  max-width: 720px;
}

/* ---------------------------------- demo --------------------------------- */

.demoGrid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  align-items: center;
}

@media (min-width: 1024px) {
  .demoGrid {
    grid-template-columns: 1fr 64px 1fr;
    gap: 24px;
  }
}

.demoCard {
  background: var(--color-surface);
  border: 2px solid var(--color-ink);
  padding: 22px 22px 20px;
}

.demoCardIn {
  box-shadow: 6px 6px 0 var(--color-chalk);
}

.demoCardOut {
  box-shadow: 6px 6px 0 var(--color-hivis);
}

.demoLabel {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 14px;
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--color-sub);
}

.demoLabel svg {
  color: var(--color-hivis);
}

.transcript {
  margin: 0;
  font-size: 16px;
  line-height: 1.7;
  color: var(--color-ink);
}

.mark {
  background: var(--color-chalkTint, rgba(36, 86, 166, 0.1));
  color: inherit;
  padding: 1px 4px;
  box-shadow: inset 0 -2px 0 var(--color-chalk);
}

.miniWave {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 34px;
  margin-top: 18px;
  overflow: hidden;
}

.demoArrow {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-hivis);
  transform: rotate(90deg);
}

@media (min-width: 1024px) {
  .demoArrow {
    transform: none;
  }
}

.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.tabBtn {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 10px 14px;
  min-height: 44px;
  border: 2px solid var(--color-border);
  background: transparent;
  color: var(--color-sub);
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;
}

.tabBtn:hover {
  border-color: var(--color-ink);
  color: var(--color-ink);
}

.tabBtn[aria-pressed='true'] {
  background: var(--color-ink);
  border-color: var(--color-ink);
  color: var(--color-bg);
}

.noteStage {
  min-height: 264px;
}

@media (min-width: 1024px) {
  .noteStage {
    min-height: 288px;
  }
}

.noteTitle {
  margin: 0 0 14px;
  font-family: var(--font-cond);
  font-weight: 600;
  font-size: 24px;
  line-height: 1.1;
  text-transform: uppercase;
  letter-spacing: 0.01em;
  color: var(--color-ink);
}

.noteBlockLabel {
  margin: 14px 0 6px;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-hivis);
}

.noteText {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: var(--color-ink);
}

.noteList {
  margin: 0;
  padding: 0;
  list-style: none;
}

.noteBullet {
  position: relative;
  padding-left: 16px;
  margin-bottom: 6px;
  font-size: 15px;
  line-height: 1.55;
  color: var(--color-ink);
}

.noteBullet::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.55em;
  width: 6px;
  height: 6px;
  background: var(--color-ink);
}

.actionRow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 15px;
  line-height: 1.5;
  color: var(--color-ink);
}

.checkbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex: none;
  border: 2px solid var(--color-ink);
  background: var(--color-surface);
  color: #14140f;
}

.checkboxDone {
  background: var(--color-hivis);
}

.dueChip {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 3px 7px;
  border: 1.5px solid var(--color-border);
  color: var(--color-sub);
}

.chipRow {
  display: flex;
  gap: 8px;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 2px solid var(--color-border);
}

.chip {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 6px 10px;
  border: 1.5px solid var(--color-border);
  color: var(--color-sub);
}

/* ------------------------------ templates ------------------------------- */

.templateChips {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.templateChip {
  display: inline-flex;
  align-items: center;
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 12px 16px;
  min-height: 44px;
  border: 2px solid var(--color-ink);
  background: var(--color-surface);
  color: var(--color-ink);
  box-shadow: 3px 3px 0 var(--color-hivis);
  transition:
    box-shadow 0.18s ease,
    transform 0.18s ease;
}

.templateChip:hover {
  box-shadow: 5px 5px 0 var(--color-hivis);
}

.templateNote {
  margin: 28px 0 0;
  font-family: var(--font-mono);
  font-size: 13px;
  letter-spacing: 0.06em;
  color: var(--color-sub);
}

/* -------------------------------- features ------------------------------- */

.featGrid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
}

@media (min-width: 768px) {
  .featGrid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .featGrid {
    grid-template-columns: repeat(3, 1fr);
    gap: 22px;
  }

  .featCardWide {
    grid-column: span 2;
  }
}

.featCard {
  height: 100%;
  background: var(--color-surface);
  border: 2px solid var(--color-border);
  padding: 24px 22px 26px;
  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    border-color 0.18s ease;
}

.featCard:hover {
  transform: translate(-3px, -3px);
  border-color: var(--color-ink);
  box-shadow: 6px 6px 0 var(--color-hivis);
}

.featTop {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.featIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 2px solid var(--color-ink);
  background: var(--color-tape);
  color: var(--color-ink);
}

.featIndex {
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.1em;
  color: var(--color-ghost);
}

.featTitle {
  margin: 0 0 8px;
  font-family: var(--font-cond);
  font-weight: 600;
  font-size: 24px;
  line-height: 1.05;
  text-transform: uppercase;
  letter-spacing: 0.01em;
  color: var(--color-ink);
}

.featSub {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: var(--color-sub);
}

/* -------------------------------- platform ------------------------------- */

.platformGrid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
}

@media (min-width: 768px) {
  .platformGrid {
    grid-template-columns: repeat(2, 1fr);
    gap: 22px;
  }
}

@media (min-width: 1024px) {
  .platformGrid {
    grid-template-columns: repeat(4, 1fr);
  }
}

.platformCard {
  height: 100%;
  background: var(--color-surface);
  border: 2px solid var(--color-border);
  padding: 24px 20px 26px;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease;
}

.platformCard:hover {
  border-color: var(--color-ink);
  box-shadow: 6px 6px 0 var(--color-chalk);
}

.platformIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: 16px;
  border: 2px solid var(--color-ink);
  background: var(--color-hivis);
  color: #14140f;
}

.platformTitle {
  margin: 0 0 8px;
  font-family: var(--font-cond);
  font-weight: 600;
  font-size: 20px;
  line-height: 1.1;
  text-transform: uppercase;
  letter-spacing: 0.01em;
  color: var(--color-ink);
}

.platformSub {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: var(--color-sub);
}

/* ------------------------------ how it works ----------------------------- */

.howGrid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 28px;
}

@media (min-width: 768px) {
  .howGrid {
    grid-template-columns: repeat(3, 1fr);
    gap: 32px;
  }
}

.howStep {
  height: 100%;
  border-top: 2px solid var(--color-ink);
  padding-top: 20px;
}

.howNum {
  display: block;
  font-family: var(--font-cond);
  font-weight: 700;
  font-size: clamp(56px, 7vw, 84px);
  line-height: 1;
  color: transparent;
  -webkit-text-stroke: 2px var(--color-hivis);
}

.howTitle {
  margin: 14px 0 8px;
  font-family: var(--font-cond);
  font-weight: 600;
  font-size: 26px;
  line-height: 1.05;
  text-transform: uppercase;
  color: var(--color-ink);
}

.howSub {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: var(--color-sub);
  max-width: 320px;
}

/* --------------------------------- pricing ------------------------------- */

.priceGrid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 22px;
  align-items: stretch;
  max-width: 880px;
}

@media (min-width: 768px) {
  .priceGrid {
    grid-template-columns: 1fr 1fr;
    gap: 28px;
  }
}

.priceGrid > div {
  height: 100%;
}

.priceCard {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--color-surface);
  border: 2px solid var(--color-ink);
  padding: 30px 26px;
}

.priceCardPro {
  background: var(--color-ink);
  color: var(--color-bg);
  box-shadow: 8px 8px 0 var(--color-hivis);
}

.priceBadge {
  position: absolute;
  top: -14px;
  left: 22px;
  margin: 0;
  padding: 5px 10px;
  background: var(--color-hivis);
  color: #14140f;
  border: 2px solid var(--color-ink);
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.priceName {
  margin: 0 0 6px;
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--color-sub);
}

.priceCardPro .priceName {
  color: var(--color-hivis);
}

.priceAmt {
  margin: 0;
  font-family: var(--font-cond);
  font-weight: 700;
  font-size: clamp(44px, 5vw, 58px);
  line-height: 1;
}

.pricePer {
  font-family: var(--font-mono);
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.04em;
}

.priceNote {
  margin: 10px 0 0;
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.05em;
  color: var(--color-sub);
}

.priceCardPro .priceNote {
  color: var(--color-bg);
  opacity: 0.75;
}

.priceList {
  margin: 22px 0 26px;
  padding: 18px 0 0;
  list-style: none;
  border-top: 2px solid var(--color-border);
  flex: 1;
}

.priceCardPro .priceList {
  border-top-color: color-mix(in srgb, var(--color-bg) 25%, transparent);
}

.priceItem {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 11px;
  font-size: 15px;
  line-height: 1.4;
}

.priceItem svg {
  flex: none;
  color: var(--color-green);
}

.priceCardPro .priceItem svg {
  color: var(--color-hivis);
}

.priceCta {
  width: 100%;
}

.priceCardPro .btnGhost {
  color: var(--color-bg);
  border-color: var(--color-bg);
}

/* --------------------------------- CTA band ------------------------------ */

.ctaBand {
  background: var(--color-hivis);
  border-block: 2px solid var(--color-ink);
}

.ctaBandInner {
  max-width: 1160px;
  margin-inline: auto;
  padding: clamp(72px, 10vw, 120px) clamp(20px, 5vw, 48px);
  text-align: center;
}

.ctaBandTitle {
  margin: 0;
  font-family: var(--font-cond);
  font-weight: 700;
  font-size: clamp(2.6rem, 7vw, 5.4rem);
  line-height: 0.98;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  color: #14140f;
}

.ctaBandSub {
  margin: 16px 0 0;
  font-family: var(--font-mono);
  font-size: 14px;
  letter-spacing: 0.08em;
  color: #14140f;
}

.ctaBandBtn {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin-top: 34px;
  font-family: var(--font-cond);
  font-weight: 600;
  font-size: 20px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: #14140f;
  color: #f7f5f0;
  border: 2px solid #14140f;
  padding: 15px 30px;
  min-height: 56px;
  box-shadow: 5px 5px 0 #f7f5f0;
  transition:
    transform 0.16s ease,
    box-shadow 0.16s ease;
}

.ctaBandBtn:hover {
  transform: translate(-2px, -2px);
  box-shadow: 7px 7px 0 #f7f5f0;
}

.ctaBandBtn:active {
  transform: translate(2px, 2px);
  box-shadow: 1px 1px 0 #f7f5f0;
}

.ctaBandBtn:focus-visible {
  outline: 2px solid #14140f;
  outline-offset: 3px;
}

/* --------------------------------- footer -------------------------------- */

.footer {
  border-top: 2px solid var(--color-border);
  background: var(--color-bg);
}

.footerInner {
  max-width: 1160px;
  margin-inline: auto;
  padding: 32px clamp(20px, 5vw, 48px);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.footerLinks {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.footerLink {
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-decoration: none;
  color: var(--color-sub);
  padding: 6px 0;
  transition: color 0.15s ease;
}

.footerLink:hover {
  color: var(--color-hivis);
}

.footerCopy {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.06em;
  color: var(--color-ghost);
}
